import { useCallback, useState } from "react";

import { Icon } from "@blueprintjs/core";

import { useAuth } from "@/components/lib/auth";
import { AddPermissionsView } from "@/components/pieces/roleplays/sharing/AddPermissionsView";
import { Button } from "@/components/ui/button";
import { ShareDialog, ShareSelector } from "@/components/ui/sharing";
import {
  FullRoleplaySession,
  ScenarioRoleplaySession,
} from "@/types/roleplays";

import { ManagePermissionsView } from "./ManagePermissionsView";
import { useSessionMutations } from "./useSessionMutations";
import { useSessionShareState } from "./useSessionShareState";

interface RoleplaySessionShareModalProps {
  session: FullRoleplaySession | ScenarioRoleplaySession;
  isOpen: boolean;
  onClose: () => void;
}

export function RoleplaySessionShareModal({
  session,
  isOpen,
  onClose,
}: RoleplaySessionShareModalProps) {
  const { currentWorkspace } = useAuth();
  const [selectorKey, setSelectorKey] = useState(0);

  // State management
  const { state, setSelectedUsers, setNotification, resetAll } =
    useSessionShareState({ session });

  // Close handler with cleanup
  const handleClose = useCallback(() => {
    onClose();
    resetAll();
    setSelectorKey((prev) => prev + 1);
  }, [onClose, resetAll]);

  // Mutation handlers
  const { inviteLoading, revokeLoading, inviteUsers, revokeAccess } =
    useSessionMutations({
      session,
      onSuccess: handleClose,
    });

  // Mode checks
  const isAddMode = state.selectedUsers.length > 0;
  const loading = inviteLoading || revokeLoading;

  // Handle back button click
  const handleBack = useCallback(() => {
    setSelectedUsers([]);
    setSelectorKey((prev) => prev + 1);
  }, [setSelectedUsers]);

  // Dialog title with back button when in add mode
  const dialogTitle = isAddMode ? (
    <div className="flex items-center gap-1 -mx-2">
      <Button
        variant="minimal"
        intent="secondary"
        size="icon"
        onClick={handleBack}
      >
        <Icon icon="arrow-left" size={16} style={{ margin: 0 }} />
      </Button>
      <div className="text-lg">Share Session</div>
    </div>
  ) : (
    "Share Session"
  );

  return (
    <ShareDialog open={isOpen} onOpenChange={handleClose} title={dialogTitle}>
      {!currentWorkspace.isPersonal && (
        <ShareSelector
          key={selectorKey}
          onChange={(users) => setSelectedUsers(users)}
          showPermissionSelector={false}
          searchIndices={["users"]}
          className="mb-5"
        />
      )}

      {isAddMode ? (
        <AddPermissionsView
          selectedUsers={state.selectedUsers}
          isNotified={state.isNotified}
          message={state.message}
          onNotificationChange={(isNotified) =>
            setNotification(isNotified, state.message)
          }
          onMessageChange={(message) =>
            setNotification(state.isNotified, message)
          }
          onShare={() => inviteUsers(state)}
          onCancel={handleBack}
          loading={loading}
        />
      ) : (
        <ManagePermissionsView
          sharedMembers={state.sharedMembers}
          programs={state.programs}
          owner={state.owner}
          isAutoShared={session.isAutoShared ?? undefined}
          onRevokeAccess={revokeAccess}
          loading={loading}
          onDone={handleClose}
        />
      )}
    </ShareDialog>
  );
}
